import React, { useState } from 'react';
import { Clock, Plus } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { UpgradePrompt } from '../Premium/UpgradePrompt';
import type { Task } from '../../types';

interface DailyTaskViewProps {
  tasks: Task[];
  onAddTask: () => void;
  onEditTask: (task: Task) => void;
  onTaskMove?: (taskId: string, hour: number) => void;
}

export function DailyTaskView({ tasks, onAddTask, onEditTask, onTaskMove }: DailyTaskViewProps) {
  const { hasFeature, showUpgradePrompt } = useSubscription();
  const isPremium = hasFeature('daily_task_view');
  const [selectedHour, setSelectedHour] = useState<number | null>(null);

  if (!isPremium) {
    return (
      <UpgradePrompt
        feature="Daily Task View"
        description="Get a detailed view of your tasks organized by time of day. Perfect for daily planning and time management."
        onUpgrade={() => showUpgradePrompt('daily_task_view')}
      />
    );
  }

  const timeSlots = Array.from({ length: 24 }, (_, i) => i);

  const getTasksForHour = (hour: number) => {
    return tasks.filter(task => {
      if (!task.duedate) return false;
      try {
        const taskDate = new Date(task.duedate);
        const today = new Date();
        return taskDate.getHours() === hour && 
               taskDate.getDate() === today.getDate() &&
               taskDate.getMonth() === today.getMonth() &&
               taskDate.getFullYear() === today.getFullYear();
      } catch (err) {
        console.error('Invalid date:', task.duedate);
        return false;
      }
    });
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination || !onTaskMove) return;

    const hour = parseInt(result.destination.droppableId);
    if (isNaN(hour)) return;

    onTaskMove(result.draggableId, hour);
  };

  const handleAddClick = (hour: number) => {
    setSelectedHour(hour);
    const dueDate = new Date();
    dueDate.setHours(hour, 0, 0, 0);
    localStorage.setItem('selectedTaskTime', dueDate.toISOString());
    onAddTask();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Daily Schedule</h2>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="space-y-2">
          {timeSlots.map((hour) => {
            const tasksForHour = getTasksForHour(hour);
            const formattedHour = new Date(2024, 0, 1, hour).toLocaleTimeString('en-US', {
              hour: 'numeric',
              hour12: true,
            });

            return (
              <Droppable key={hour} droppableId={hour.toString()}>
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`p-4 rounded-xl ${
                      tasksForHour.length > 0 ? 'bg-[#2A2B2E]' : 'bg-black/20'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2 text-sm text-gray-400 w-20">
                        <Clock className="w-4 h-4" />
                        <span>{formattedHour}</span>
                      </div>
                      
                      <div className="flex-1">
                        {tasksForHour.length > 0 ? (
                          <div className="space-y-2">
                            <button
                              onClick={() => handleAddClick(hour)}
                              className="w-full p-2 bg-black/10 rounded-lg hover:bg-black/20 transition-colors text-sm text-gray-400 flex items-center justify-center gap-2"
                            >
                              <Plus className="w-4 h-4" />
                              Add Task
                            </button>
                            {tasksForHour.map((task, index) => (
                              <Draggable
                                key={task.id}
                                draggableId={task.id}
                                index={index}
                              >
                                {(provided) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    onClick={() => onEditTask(task)}
                                    className="p-3 bg-black/20 rounded-lg cursor-pointer hover:bg-black/30 transition-colors"
                                  >
                                    <div className="flex items-center justify-between">
                                      <span className="font-medium">{task.title}</span>
                                      <span className={`text-xs px-2 py-1 rounded-full ${
                                        task.priority === 'high' ? 'bg-red-500/20 text-red-500' :
                                        task.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-500' :
                                        'bg-green-500/20 text-green-500'
                                      }`}>
                                        {task.priority}
                                      </span>
                                    </div>
                                    {task.description && (
                                      <p className="text-sm text-gray-400 mt-1">{task.description}</p>
                                    )}
                                  </div>
                                )}
                              </Draggable>
                            ))}
                          </div>
                        ) : (
                          <div className="h-8 flex items-center">
                            <button
                              onClick={() => handleAddClick(hour)}
                              className="w-full p-2 bg-black/10 rounded-lg hover:bg-black/20 transition-colors text-sm text-gray-400 flex items-center justify-center gap-2"
                            >
                              <Plus className="w-4 h-4" />
                              Add Task
                            </button>
                          </div>
                        )}
                        {provided.placeholder}
                      </div>
                    </div>
                  </div>
                )}
              </Droppable>
            );
          })}
        </div>
      </DragDropContext>
    </div>
  );
}